import { useState, useEffect, useMemo, useRef } from "react";
import { toast } from "react-toastify";
import Select from "../../components/common/Select";
import api from "../../services/api";
import DataTable from "../../components/common/DataTable";
import { getSupportTicketsDashboard, getSupportFormData, fetchSupportTransaction, submitSupportTicket, getSupportTickets, getTicketDetail } from "../../services/supportService";



const SearchIcon = () => (
  <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" strokeLinecap="round" />
  </svg>
);

const DownloadIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M12 4v12m-4-4l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PlusIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
    <path d="M12 5v14M5 12h14" strokeLinecap="round" />
  </svg>
);

const statusStyleMap = (statusKey) => {
  if (statusKey === "open")        return { text: "text-blue-600",    bg: "bg-blue-50",    border: "border-blue-200",    dot: "bg-blue-400" };
  if (statusKey === "in_progress") return { text: "text-amber-600",   bg: "bg-amber-50",   border: "border-amber-200",   dot: "bg-amber-400" };
  if (statusKey === "resolved")    return { text: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-200", dot: "bg-emerald-400" };
  return                                  { text: "text-gray-600",    bg: "bg-gray-50",    border: "border-gray-200",    dot: "bg-gray-400" };
};

function TicketDetailModal({ ticketId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imgPreview, setImgPreview] = useState(null);

  useEffect(() => {
    getTicketDetail(ticketId)
      .then(res => setData(res.data?.data || null))
      .catch(() => setError("Failed to load ticket details."))
      .finally(() => setLoading(false));
  }, [ticketId]);

  const sStyle = data ? statusStyleMap(data.status?.toLowerCase()) : {};

  return (
    <div onClick={onClose} className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden">

        {/* Header */}
        <div className="bg-gradient-to-r from-[#754F89] to-[#4B2364] px-6 py-5 relative">
          <button onClick={onClose} className="absolute top-3 right-3 w-7 h-7 rounded-full bg-white/20 flex items-center justify-center border-none cursor-pointer text-white text-sm hover:bg-white/30">
            ✕
          </button>
          <p className="text-[11px] font-bold text-white/60 uppercase tracking-widest mb-1">Ticket Details</p>
          <p className="text-[15px] font-bold text-white font-mono">{loading ? "Loading..." : data?.ticket_id?.toUpperCase() || ticketId}</p>
        </div>

        {/* Body */}
        <div className="px-6 py-5 max-h-[70vh] overflow-y-auto space-y-4">
          {loading && <div className="text-center py-10 text-gray-400 text-sm">Loading...</div>}
          {error  && <div className="text-center py-10 text-red-500 text-sm">{error}</div>}

          {data && !loading && (
            <>
              {/* Status + Date */}
              <div className="flex items-center justify-between">
                <span className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-3 py-1 rounded-full border ${sStyle.bg} ${sStyle.text} ${sStyle.border}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${sStyle.dot}`} />
                  {data.status?.replace("_", " ").toUpperCase()}
                </span>
                <span className="text-[11px] text-gray-400 font-medium">
                  {data.created_at ? new Date(data.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—"}
                </span>
              </div>

              {/* Subject */}
              <div className="bg-[#F9F5FF] rounded-[12px] px-4 py-3">
                <p className="text-[11px] font-bold text-[#9B8FA0] uppercase mb-1">Subject</p>
                <p className="text-[14px] font-semibold text-[#34094D]">{data.subject}</p>
              </div>

              {/* Description */}
              <div>
                <p className="text-[11px] font-bold text-[#9B8FA0] uppercase mb-1">Description</p>
                <p className="text-[13px] text-[#4C444E] leading-relaxed">{data.description}</p>
              </div>

              {/* Admin Note */}
              {data.admin_note && (
                <div className="bg-amber-50 border border-amber-100 rounded-[12px] px-4 py-3">
                  <p className="text-[11px] font-bold text-amber-600 uppercase mb-1">Admin Note</p>
                  <p className="text-[13px] text-amber-800">{data.admin_note}</p>
                </div>
              )}

              {/* Resolved At */}
              {data.resolved_at && (
                <div className="flex items-center gap-2 text-[12px] text-emerald-600 font-semibold">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  Resolved at: {new Date(data.resolved_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                </div>
              )}

              {/* Proofs */}
              {data.proofs?.length > 0 && (
                <div>
                  <p className="text-[11px] font-bold text-[#9B8FA0] uppercase mb-2">Attached Proofs</p>
                  <div className="flex flex-wrap gap-3">
                    {data.proofs.map((url, i) => (
                      <img
                        key={i}
                        src={url}
                        alt={`proof-${i + 1}`}
                        onClick={() => setImgPreview(url)}
                        className="w-16 h-16 rounded-xl object-cover border border-[#E1E3E4] cursor-pointer hover:opacity-80 transition"
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Image Lightbox */}
      {imgPreview && (
        <div onClick={() => setImgPreview(null)} className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80">
          <img src={imgPreview} alt="preview" className="max-w-[90vw] max-h-[90vh] rounded-xl shadow-2xl" />
        </div>
      )}
    </div>
  );
}

function RaiseComplaint({ onBack }) {
  const [txnId, setTxnId] = useState("");
  // const [txnType, setTxnType] = useState("");
  const [txnFound, setTxnFound] = useState(null);
  const [lookupDone, setLookupDone] = useState(false);
  const [service, setService] = useState("");
  // const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState([]);
  const [fileError, setFileError] = useState("");
  const [subjects, setSubjects] = useState([]);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(null);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    getSupportFormData()
      .then((res) => setSubjects(res.data?.data?.subjects || []))
      .catch(() => {});
  }, []);

  const handleSubmit = async () => {
    if (!txnFound) return;
    if (!service) { setSubmitError("Please select a complaint reason."); return; }
    if (!description.trim()) { setSubmitError("Please enter a description."); return; }
    setSubmitLoading(true);
    setSubmitError(null);
    setSubmitSuccess(null);
    try {
      const res = await submitSupportTicket({
        transactionId: txnFound.id,
        subject:       service,
        description:   description.trim(),
        files,
      });
      const d = res.data?.data;
      setSubmitSuccess(`Ticket #${d?.ticket_id?.slice(0, 8).toUpperCase()} submitted successfully!`);
      setTimeout(() => onBack(), 2000);
    } catch (err) {
      setSubmitError(err.response?.data?.message || "Submission failed. Please try again.");
    } finally {
      setSubmitLoading(false);
    }
  };

  const [lookupLoading, setLookupLoading] = useState(false);

  const handleLookup = async () => {
    if (!txnId.trim()) return;
    setLookupLoading(true);
    setLookupDone(false);
    setTxnFound(null);
    try {
      const res = await fetchSupportTransaction(txnId.trim());
      const d = res.data?.data;
      if (d) {
        setTxnFound({
          id:          d.transaction_id,
          reference:   d.reference_id,
          date:        d.created_at ? new Date(d.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—",
          amount:      d.amount ? `₹${parseFloat(d.amount).toLocaleString("en-IN")}` : "—",
          status:      d.status,
          type:        typeof d.service === 'object' ? (d.service?.name || '—') : (d.service || '—'),
        });
      } else {
        setTxnFound(null);
      }
    } catch {
      setTxnFound(null);
    } finally {
      setLookupDone(true);
      setLookupLoading(false);
    }
  };

  return (
    <div className="flex-1 px-8 py-7 overflow-y-auto">
      {/* Page Title */}
      <div className="flex items-center gap-3 mb-2">
        <button onClick={onBack} className="w-9 h-9 flex items-center justify-center transition-colors cursor-pointer">
          <svg className="w-7 h-7 text-[#4B2364]" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
            <path d="M15 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <div>
          <h1 className="text-[36px] font-extrabold text-[#4B2364] leading-tight">Raise Complaint</h1>
        </div>
      </div>
      <p className="text-[18px] text-[#4C444E] font-medium pl-3">Start by finding the transaction related to your issue. Providing a reference helps us locate
        the record immediately.</p>

      {/* Step indicators */}
      <div className="flex items-center gap-3 mb-7 mt-5">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-primary-gradient flex items-center justify-center text-white text-[12px] font-bold">1</div>
          <span className="text-[13px] font-bold text-primary">Transaction Lookup</span>
        </div>
        <div className="flex-1 h-px bg-[#E5E7EB] max-w-[60px]" />
        <div className="flex items-center gap-2">
          <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[12px] font-bold ${txnFound ? "bg-primary-gradient text-white" : "bg-[#F3F4F5] text-[#7D747F]"
            }`}>2</div>
          <span className={`text-[13px] font-bold ${txnFound ? "text-primary" : "text-[#7D747F]"}`}>Complaint Submission</span>
        </div>
      </div>

      <div className="space-y-5">
        {/* Step 1 — Transaction Lookup */}
        <div className="bg-white rounded-[16px] px-6 py-[28px] border border-[#E1E3E480] shadow-[0px_1px_2px_0px_#0000000D] overflow-hidden">
          <div className="flex items-center mb-4">
            <div className="w-8 h-8 flex items-center justify-center">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
              </svg>
            </div>
            <div>
              <p className="text-[20px] font-semibold text-[#34094D]">Step 1: Find Transaction</p>
            </div>
          </div>
          <div className="p-2 w-3/4 block">
            <div className="grid grid-cols-4 block gap-4 mb-4 items-end">
              <div className="col-span-3">
                <label className="text-[14px] font-semibold text-[#4C444E] uppercase tracking-wider mb-1.5">Transaction ID / Reference Number</label>
                <input
                  value={txnId}
                  onChange={e => { setTxnId(e.target.value); setLookupDone(false); setTxnFound(null); }}
                  placeholder="# TXN-45920-ZM"
                  className="w-full bg-[#F3F4F5] rounded-[24px] border border-[#CEC3D0] px-[20px] py-[12px] text-[16px] text-[#191C1D] outline-none focus:ring-1 focus:ring-[#4B2364]/30 placeholder:text-[#7D747F66] transition-all"
                />
              </div>
              <div>
                <button
                  onClick={handleLookup}
                  disabled={lookupLoading}
                  className="flex items-center gap-2 text-[13px] font-bold text-white bg-primary px-[28px] py-[16px] rounded-[24px] border-none cursor-pointer hover:opacity-90 transition-all shadow-[0px_4px_12px_rgba(75,35,100,0.25)] disabled:opacity-60"
                >
                  {lookupLoading ? "Searching..." : "Fetch Record"}
                  {!lookupLoading && (
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 12a9 9 0 11-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
                      <path d="M21 3v5h-5" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </div>
          {/* Result */}
          {lookupDone && !txnFound && (
            <div className="mt-4 flex items-center gap-2 bg-red-50 border border-red-100 rounded-[12px] px-4 py-3">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="9" /><path d="M12 8v4M12 16h.01" /></svg>
              <p className="text-[12px] font-semibold text-red-600">No transaction found with this ID. Please check and try again.</p>
            </div>
          )}

          {txnFound && (
            <div className="mt-2 bg-[#FAF5FF] rounded-[24px] border border-[#F3E8FF] p-6">
              <div className="grid grid-cols-4 gap-4">
                {[
                  { label: "Status",         value: txnFound.status },
                  { label: "Amount",         value: txnFound.amount },
                  { label: "Service",        value: txnFound.type },
                  { label: "Date & Time",    value: txnFound.date },
                ].map(f => (
                  <div key={f.label}>
                    <p className="text-[12px] font-semibold text-[#C084FC] uppercase tracking-wider mb-1">{f.label}</p>
                    <p className="text-[13px] font-semibold text-[#191C1D] truncate">{f.value}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Step 2 — Complaint Submission */}
        <div className={`overflow-hidden transition-all ${txnFound ? "" : "opacity-40 pointer-events-none"
          }`}>

          {/* Two-column body */}
          <div className="grid grid-cols-5 gap-6">

            {/* LEFT — Reason Selection */}
            <div className="col-span-2 p-7 bg-[#F3F4F5] border border-[#E1E3E480] rounded-[16px] shadow-[0px_1px_2px_0px_#0000000D] flex flex-col">
              {/* Card Title */}
              <div className="mb-5 px-1">
                <p className="text-[20px] font-semibold text-[#34094D]">
                  Step 2: Complaint Reason
                  <span className="text-[#4B2364] ml-0.5">*</span>
                </p>
              </div>

              {/* Radio List */}
              <div className="flex flex-col gap-3">
                {subjects.length === 0 ? (
                  <p className="text-[13px] text-[#9B8FA0] px-1">Loading...</p>
                ) : subjects.map((label) => {
                  const isSelected = service === label;
                  return (
                    <button
                      key={label}
                      onClick={() => setService(label)}
                      className={`w-full flex items-center gap-3 px-4 py-4 rounded-[16px] text-left transition-all cursor-pointer ${isSelected
                          ? "bg-[#FAF5FF] border border-[#CFA9F3]"
                          : "bg-white border border-transparent"
                        }`}
                    >
                      <div
                        className={`w-[20px] h-[20px] rounded-full bg-[#EDEEEF] flex items-center justify-center border-2 transition-all
                          ${isSelected ? "border-[#5B2A86]" : "border-[#CEC3D0]"}`}
                      >
                        {isSelected && <div className="w-[10px] h-[10px] bg-[#5B2A86] rounded-full" />}
                      </div>
                      <p className="text-[15px] font-medium text-[#2D0A3A]">{label}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* RIGHT — Complaint Description */}
            <div className="col-span-3 p-7 flex flex-col gap-6 bg-white rounded-2xl shadow-sm">

              {/* Description */}
              <div className="flex flex-col gap-2">
                <label className="block text-[16px] font-semibold text-[#34094D] uppercase  mb-2">
                  Complaint Description
                </label>
                <textarea
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  rows={5}
                  placeholder="Explain what happened..."
                  className="w-full bg-[#F3F4F5] rounded-[24px] border border-[#CEC3D0] px-4 py-3.5 text-[14px] text-[#191C1D] outline-none focus:ring-1 focus:ring-[#4B2364]/20 focus:border-[#4B2364]/30 placeholder:text-[#C4B8CC] resize-none transition-all leading-relaxed"
                />
              </div>

              {/* Attach Proof */}
              <div>
                <div className="mb-3">
                  <p className="text-[20px] font-bold text-[#34094D]">
                    Attach Proof
                    <span className="ml-1.5 text-[14px] font-regular text-[#94A3B8]">
                      (Optional)
                    </span>
                  </p>
                </div>

                {fileError && (
                  <div className="flex items-center gap-2 bg-red-50 border border-red-100 rounded-[10px] px-3 py-2 mb-2">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="9"/><path d="M12 8v4M12 16h.01"/></svg>
                    <p className="text-[12px] font-semibold text-red-600">{fileError}</p>
                  </div>
                )}

                <div className="flex flex-wrap gap-3">

                  {/* Files */}
                  {files.map((f, i) => (
                    <div
                      key={i}
                      className="relative w-[64px] h-[64px] rounded-xl overflow-hidden border border-[#E1E3E4] bg-[#F9FAFB] group"
                    >
                      {f.type.startsWith("image/") ? (
                        <img
                          src={URL.createObjectURL(f)}
                          alt={f.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center gap-1">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4B2364" strokeWidth="1.8">
                            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                            <polyline points="14 2 14 8 20 8" />
                          </svg>
                          <p className="text-[8px] font-semibold text-[#4B2364] uppercase truncate w-full text-center px-1">
                            {f.name.split(".").pop()}
                          </p>
                        </div>
                      )}

                      {/* Remove */}
                      <button
                        onClick={() => setFiles(prev => prev.filter((_, idx) => idx !== i))}
                        className="absolute top-1 right-1 w-5 h-5 rounded-full bg-[#34094D] flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                      >
                        <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                          <path d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}

                  {/* Add Button */}
                  <label className="w-[64px] h-[64px] rounded-xl border-2 border-dashed border-[#D1D5DB] bg-[#F9FAFB] flex flex-col items-center justify-center cursor-pointer hover:border-[#4B2364] hover:bg-[#FAF5FF] transition group">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9B8FA0" strokeWidth="2.5" className="group-hover:stroke-[#4B2364] transition">
                      <path d="M12 5v14M5 12h14" />
                    </svg>
                    <input
                      type="file"
                      className="hidden"
                      accept=".jpg,.jpeg,.png"
                      multiple
                      onChange={e => {
                        const picked = Array.from(e.target.files || []);
                        const oversized = picked.filter(f => f.size > 1 * 1024 * 1024);
                        if (oversized.length > 0) {
                          setFileError(`File "${oversized[0].name}" exceeds 1MB limit. Please upload a smaller file.`);
                          e.target.value = "";
                          return;
                        }
                        setFileError("");
                        setFiles(prev => [...prev, ...picked]);
                        e.target.value = "";
                      }}
                    />
                  </label>

                </div>
              </div>

              {/* Error / Success */}
              {submitError && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-100 rounded-[12px] px-4 py-3">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="9"/><path d="M12 8v4M12 16h.01"/></svg>
                  <p className="text-[12px] font-semibold text-red-600">{submitError}</p>
                </div>
              )}
              {submitSuccess && (
                <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-100 rounded-[12px] px-4 py-3">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  <p className="text-[12px] font-semibold text-emerald-600">{submitSuccess}</p>
                </div>
              )}

              {/* Submit Button */}
              <button
                onClick={handleSubmit}
                disabled={submitLoading || !!submitSuccess}
                className="mt-2 w-fit px-7 h-[46px] rounded-full text-white text-[15px] font-semibold bg-secondary-gradient flex items-center gap-2 shadow-sm hover:opacity-95 transition disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitLoading ? "Submitting..." : submitSuccess ? "Submitted ✓" : "Submit Complaint"}
                {!submitLoading && !submitSuccess && <span className="text-lg">➤</span>}
              </button>

            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default function ComplaintsDashboard() {
  const [view, setView] = useState("dashboard");
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [status, setStatus] = useState("All Status");
  const [service, setService] = useState("All Services");
  const [dashStats, setDashStats] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [loadingTickets, setLoadingTickets] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [showFormatMenu, setShowFormatMenu] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setShowFormatMenu(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);
  const [limit, setLimit] = useState(10);

  const normalizePagination = (raw, fallbackPage, fallbackLimit, rowCount) => {
    const p = raw || {};
    const current = Number(p.current_page ?? p.page) || fallbackPage;
    const total = Number(p.total) || 0;
    const lastByTotal = total > 0 ? Math.ceil(total / fallbackLimit) : 0;
    const last = Number(p.last_page ?? p.total_pages) || lastByTotal || (rowCount === fallbackLimit ? current + 1 : current);
    return {
      current_page: current,
      last_page: Math.max(1, last),
      total,
      has_more: Boolean(p.has_more ?? current < Math.max(1, last)),
    };
  };

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [status, service]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      setSearch(searchInput.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    getSupportTicketsDashboard()
      .then((res) => setDashStats(res.data?.data))
      .catch(() => {});
  }, [view]);

  const handleExport = async (format = 'csv') => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (status !== 'All Status') params.append('status', status);
      params.append('format', format);
      const res = await api.get(`/support-tickets/export?${params.toString()}`, { responseType: 'blob' });

      const contentType = res.headers?.['content-type'] || '';
      if (contentType.includes('application/json')) {
        const text = await res.data.text();
        const json = JSON.parse(text);
        toast.info(json.message || 'Export queued. Check Reports section to download.');
        return;
      }

      const url  = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href  = url;
      const ext = format === 'excel' ? 'xlsx' : 'csv';
      link.setAttribute('download', `support_tickets_${new Date().toISOString().split('T')[0]}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export completed successfully!');
    } catch {
      toast.error('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };


  useEffect(() => {
    setLoadingTickets(true);
    getSupportTickets({
      status: status === "All Status" ? "" : status,
      service: service === "All Services" ? "" : service,
      search,
      page,
      limit,
    })
      .then((res) => {
        const rows = res.data?.data?.tickets || [];
        const normalized = normalizePagination(res.data?.data?.pagination || res.data?.data?.meta, page, limit, rows.length);
        setTickets(rows);
        setPagination(normalized);
        setPage(normalized.current_page);
      })
      .catch(() => {})
      .finally(() => setLoadingTickets(false));
  }, [page, limit, view, status, service, search]);

  const columns = useMemo(() => [
    {
      id: "sr",
      header: "Sr No",
      size: 70,
      Cell: ({ row }) => (
        <span className="text-[13px] text-[#4C444E] font-semibold">
          {(page - 1) * limit + row.index + 1}
        </span>
      ),
    },
    {
      accessorKey: "ticket_id",
      header: "Ticket ID",
      Cell: ({ row }) => (
        <div>
          <p className="text-[13px] font-bold text-primary textmax-w-[120px] truncate cursor-pointer">{row.original.ticket_id?.toUpperCase()}</p>
          <p className="text-[12px] text-[#4C444E] mt-0.5">
            {row.original.created_at ? new Date(row.original.created_at).toLocaleDateString("en-IN") : "—"}
          </p>
        </div>
      ),
    },
    {
      accessorKey: "subject",
      header: "Subject",
      Cell: ({ row }) => (
        <span className="text-[13px] text-[#191C1D] font-medium">{row.original.subject || "—"}</span>
      ),
    },
    {
      accessorKey: "service",
      header: "Service",
      Cell: ({ row }) => (
        <span className="text-[13px] bg-primary/5 font-semibold text-primary px-3 py-1 rounded-full">
          {row.original.service || "—"}
        </span>
      ),
    },
    {
      accessorKey: "status",
      header: "Status",
      Cell: ({ row }) => {
        const s = statusStyleMap(row.original.status?.toLowerCase());
        return (
          <span className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full border ${s.bg} ${s.text} ${s.border}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
            {row.original.status?.replace("_", " ").toUpperCase()}
          </span>
        );
      },
    },
    {
      id: "actions",
      header: "Actions",
      Cell: ({ row }) => (
        <button
          onClick={() => setSelectedTicketId(row.original.ticket_id)}
          className="flex items-center gap-1.5 text-[12px] font-semibold text-primary-mid bg-primary-mid/10 hover:bg-primary-mid/20 px-3 py-1.5 rounded-lg border-none cursor-pointer transition-colors"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#4B2364" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
            <circle cx="12" cy="12" r="3"/>
          </svg>
          View
        </button>
      ),
    },
  ], [page, limit]);

  return (
    <>
      {selectedTicketId && (
        <TicketDetailModal ticketId={selectedTicketId} onClose={() => setSelectedTicketId(null)} />
      )}
        {view === "raise" ? (
          <RaiseComplaint onBack={() => setView("dashboard")} />
        ) : (
          <div className="flex-1 px-8 py-7 overflow-y-auto">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
              <div>
                <h1 className="text-[36px] font-bold text-primary mb-1.5">Complaints Dashboard</h1>
                {/* <p className="text-[18px] text-[#4C444E] font-medium mt-1.5 max-w-md">
                  Centralized hub for managing all service resolutions. Monitor ticket status, analyze response metrics, and initiate new support requests.
                </p> */}
              </div>
                <div className="flex items-center gap-3 mt-1">
                  {/* Export Split Button */}
                  <div className="relative" ref={menuRef}>
                    <div className={`flex items-center rounded-lg overflow-hidden ${exporting ? 'opacity-60 pointer-events-none' : ''}`}>
                      <button
                        onClick={() => handleExport('csv')}
                        disabled={exporting}
                        className="flex items-center gap-2 bg-[#4B2364] hover:bg-[#3a1a4f] px-4 py-[9px] text-[13px] font-semibold text-white cursor-pointer border-none transition-colors"
                      >
                        {exporting ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <DownloadIcon />
                        )}
                        {exporting ? 'Exporting...' : 'Export'}
                      </button>
                      <button
                        onClick={() => setShowFormatMenu((v) => !v)}
                        disabled={exporting}
                        className="flex items-center justify-center bg-[#4B2364] hover:bg-[#3a1a4f] px-2.5 h-[38px] text-white border-none cursor-pointer transition-colors"
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className={`transition-transform duration-200 ${showFormatMenu ? 'rotate-180' : ''}`}>
                          <polyline points="6 9 12 15 18 9" />
                        </svg>
                      </button>
                    </div>

                    {showFormatMenu && (
                      <div className="absolute right-0 top-full mt-2 z-50 bg-white rounded-xl shadow-[0_8px_24px_rgba(0,0,0,0.12)] border border-gray-100 overflow-hidden w-[160px]">
                        <div className="px-3 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wider border-b border-gray-100">
                          Choose Format
                        </div>
                        <button
                          onClick={() => { handleExport('csv'); setShowFormatMenu(false); }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-[13px] font-medium text-gray-700 hover:bg-gray-50 cursor-pointer border-none bg-transparent transition-colors"
                        >
                          <span className="w-6 h-6 rounded-md bg-emerald-50 flex items-center justify-center shrink-0">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2">
                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                              <polyline points="14,2 14,8 20,8" />
                              <line x1="8" y1="13" x2="16" y2="13" />
                              <line x1="8" y1="17" x2="16" y2="17" />
                            </svg>
                          </span>
                          CSV
                        </button>
                        <button
                          onClick={() => { handleExport('excel'); setShowFormatMenu(false); }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-[13px] font-medium text-gray-700 hover:bg-gray-50 cursor-pointer border-none bg-transparent transition-colors"
                        >
                          <span className="w-6 h-6 rounded-md bg-blue-50 flex items-center justify-center shrink-0">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2">
                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                              <polyline points="14,2 14,8 20,8" />
                              <rect x="8" y="12" width="8" height="6" rx="1" />
                            </svg>
                          </span>
                          Excel
                        </button>
                      </div>
                    )}
                  </div>
                  <button onClick={() => setView("raise")} className="flex items-center gap-2 text-sm font-semibold text-white bg-secondary-gradient shadow-[0px_12px_24px_-8px_#EA853466] px-4 py-2.5 rounded-xl transition-all">
                    <PlusIcon />
                    Raise New Complaint
                  </button>
                </div>
            </div>

            {/* Stat Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
              {[
                {
                  label: "Total Tickets", value: dashStats?.total_tickets ?? "—", sub: "All time tickets", subColor: "text-emerald-500",
                  iconBg: "bg-violet-50", iconColor: "text-violet-500",
                  icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" strokeLinecap="round" strokeLinejoin="round" /></svg>,
                },
                {
                  label: "Open", value: dashStats?.open_tickets ?? "—", sub: "Awaiting response", subColor: "text-gray-400",
                  iconBg: "bg-red-50", iconColor: "text-red-400",
                  icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" /><path d="M12 8v4M12 16h.01" strokeLinecap="round" /></svg>,
                },
                {
                  label: "In Progress", value: dashStats?.in_progress_tickets ?? "—", sub: "Being worked on", subColor: "text-gray-400",
                  iconBg: "bg-orange-50", iconColor: "text-orange-400",
                  icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 12l2 2 4-4" strokeLinecap="round" strokeLinejoin="round" /></svg>,
                },
                {
                  label: "Resolved", value: dashStats?.resolved_tickets ?? "—", sub: "Successfully closed", subColor: "text-emerald-500",
                  iconBg: "bg-emerald-50", iconColor: "text-emerald-500",
                  icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round" /></svg>,
                },
              ].map((s, i) => (
                <div key={i} className="bg-white rounded-[24px] p-6 shadow-[0px_8px_24px_0px_#4B23640A] border border-[#F0EBF4]">
                  <div className="flex items-center justify-between mb-4">
                    <div className={`w-10 h-10 rounded-[14px] flex items-center justify-center ${s.iconBg} ${s.iconColor}`}>
                      {s.icon}
                    </div>
                    <span className="text-[11px] font-bold text-[#7D747F] uppercase tracking-wider">{s.label}</span>
                  </div>
                  <p className="text-[32px] font-extrabold text-[#191C1D] leading-none mb-2">{s.value}</p>
                  <p className={`text-[12px] font-semibold ${s.subColor}`}>{s.sub}</p>
                </div>
              ))}
            </div>

            {/* Filter Bar */}
            <div className="bg-[#F3F4F5] p-5 rounded-[24px] mb-6">
              <div className="flex flex-wrap items-center justify-between gap-3">

                {/* Search */}
                <div className="flex items-center gap-3 bg-white rounded-[16px] px-4 py-3 flex-1 min-w-[220px] shadow-sm border border-[#F0EBF4]">
                  <SearchIcon />
                  <input
                    type="text"
                    placeholder="Search ticket ID or reason..."
                    className="text-[14px] text-[#191C1D] placeholder:text-[#7D747F99] outline-none bg-transparent w-full"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                  />
                  {searchInput && (
                    <button onClick={() => setSearchInput("")} className="text-[#7D747F] hover:text-[#191C1D] border-none bg-transparent cursor-pointer shrink-0">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path strokeLinecap="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>

                <div className="flex items-center gap-3 flex-wrap">
                  {/* Status Filter */}
                   <div className="relative">
                    <Select
                      className="bg-white min-w-[150px] px-3 py-2.5 rounded-[12px] border border-[#F0EBF4] shadow-sm focus-within:ring-2 focus-within:ring-violet-300"
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                      options={[
                        { label: "All Status", value: "All Status" },
                        { label: "Open", value: "open" },
                        { label: "In Progress", value: "in_progress" },
                        { label: "Resolved", value: "resolved" },
                        { label: "Closed", value: "closed" },
                      ]}
                    />
                  </div>

                  {/* Category Filter */}
                   <div className="relative">
                    <Select
                      className="bg-white min-w-[170px] px-3 py-2.5 rounded-[12px] border border-[#F0EBF4] shadow-sm focus-within:ring-2 focus-within:ring-violet-300"
                      value={service}
                      onChange={(e) => setService(e.target.value)}
                      options={[
                        { label: "All Services", value: "All Services" },
                        { label: "Payout", value: "PAYOUT" },
                        { label: "Onboard", value: "ONBOARD" },
                        { label: "Settle", value: "SETTLE" },
                      ]}
                    />
                  </div>
                  {/* <button className="text-[#4C444E] bg-[#FFFFFF]  text-[13px] font-semibold px-5 py-2.5 rounded-[12px] border-none cursor-pointer hover:opacity-90 transition-opacity">
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 4h18M7 12h10M10 20h4" strokeLinecap="round" />
                    </svg>
                  </button> */}

                  {(searchInput || status !== "All Status" || service !== "All Services") && (
                    <button
                      onClick={() => { setSearchInput(""); setSearch(""); setStatus("All Status"); setService("All Services"); }}
                      className="text-[13px] font-semibold text-[#7D747F] hover:text-primary bg-transparent border-none cursor-pointer transition-colors"
                    >
                      Reset
                    </button>
                  )}
                </div>
              </div>

              {/* Active filter tags */}
              {/* {(status !== "All Status" || service !== "All Services") && (
                <div className="flex items-center gap-2 mt-3 flex-wrap">
                  <span className="text-[11px] text-[#7D747F] font-semibold">Active:</span>
                  {status !== "All Status" && (
                    <span className="inline-flex items-center gap-1.5 bg-amber-50 text-amber-700 border border-amber-200 text-[11px] font-semibold px-2.5 py-1 rounded-full">
                      {status}
                      <button onClick={() => setStatus("All Status")} className="bg-transparent border-none cursor-pointer text-amber-500 hover:text-amber-700">
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path strokeLinecap="round" d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    </span>
                  )}
                  {service !== "All Services" && (
                    <span className="inline-flex items-center gap-1.5 bg-violet-50 text-violet-700 border border-violet-200 text-[11px] font-semibold px-2.5 py-1 rounded-full">
                      {service}
                      <button onClick={() => setService("All Services")} className="bg-transparent border-none cursor-pointer text-violet-500 hover:text-violet-700">
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path strokeLinecap="round" d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    </span>
                  )}
                </div>
              )} */}
            </div>

            {/* Table */}
            <div className="bg-white rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.03)]">
              <div className="flex flex-col sm:flex-row sm:justify-between p-4 sm:p-6 items-start sm:items-center gap-4">
                <div>
                  <h2 className="text-[20px] font-bold text-[#34094D]">All Tickets</h2>
                  <p className="text-sm text-[#4C444E]">Manage and track all your support requests</p>
                </div>
                <select
                  value={limit}
                  onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
                  className="bg-[#F3F4F5] rounded-[10px] px-3 py-2 text-[13px] text-[#191C1D] border-none outline-none cursor-pointer"
                >
                  {[10, 25, 50].map((l) => <option key={l} value={l}>Show {l}</option>)}
                </select>
              </div>

              {loadingTickets && (
                <div className="px-8 pb-3 text-xs text-gray-500">
                  {tickets.length > 0 ? "Refreshing..." : "Loading tickets..."}
                </div>
              )}

              <DataTable columns={columns} data={tickets} />

              {!loadingTickets && tickets.length === 0 && (
                <div className="text-center py-10 text-gray-400 text-sm">No tickets found.</div>
              )}
            </div>

            {/* Pagination */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between px-2 py-4 gap-3">
              <span className="text-xs text-[#4C444E]">
                {pagination && pagination.total > 0
                  ? `Showing ${(page - 1) * limit + 1}–${Math.min(page * limit, pagination.total)} of ${pagination.total} records`
                  : "Showing 0 records"}
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className={`w-8 h-8 flex items-center justify-center rounded-lg border border-[#CEC3D0] ${page === 1 ? "bg-gray-50 text-gray-300 cursor-not-allowed" : "bg-white text-[#4C444E] hover:bg-gray-50 cursor-pointer"}`}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 18l-6-6 6-6" />
                  </svg>
                </button>
                <span className="text-xs text-[#4C444E]">
                  {pagination ? `${pagination.current_page} / ${pagination.last_page}` : "1 / 1"}
                </span>
                <button
                  onClick={() => setPage(p => p + 1)}
                  disabled={!pagination?.has_more}
                  className={`w-8 h-8 flex items-center justify-center rounded-lg border border-[#CEC3D0] ${!pagination?.has_more ? "bg-gray-50 text-gray-300 cursor-not-allowed" : "bg-white text-[#4C444E] hover:bg-gray-50 cursor-pointer"}`}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 18l6-6-6-6" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}
      </>
  );
}
